if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        // Perform instant local update (0ms) of variant ID input & button state to handle rapid "Add to Cart" clicks
        this.handleLocalVariantUpdate();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        
        const isQuickView = !!this.closest('quick-add-modal') 
          || !!this.closest('.quick-add-modal') 
          || !!this.closest('.quick-add-modal__content') 
          || !!this.dataset.originalSection
          || (this.dataset.section && this.dataset.section.startsWith('quickadd'));
        if (isQuickView) {
          return;
        }


        const getBasePath = (url) => {
          if (!url) return '';
          const path = url.split('?')[0];
          return path.replace(/^(https?:)?\/\/[^\/]+/, '');
        };
        const shouldSwapProduct = getBasePath(this.dataset.url) !== getBasePath(productUrl);

        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;


        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      getActiveVariantSelects(root = this) {
        const selects = Array.from((root || this).querySelectorAll('variant-selects'));
        return selects.find((el) => el.style.display !== 'none' && !el.closest('[style*="display: none"]')) || selects[0] || root;
      }

      handleLocalVariantUpdate() {
        const isQuickView = !!this.closest('quick-add-modal');
        const selectedOptions = [];
        const activeContainer = this.getActiveVariantSelects();

        activeContainer.querySelectorAll('fieldset.product-form__input, fieldset[data-option-name], fieldset').forEach((group) => {
          if (group.closest('desktop-sticky-bar') || group.closest('[style*="display: none"]')) return;
          const checked = group.querySelector('input:checked');
          if (checked) {
            selectedOptions.push(checked.value);
          }
        });
        activeContainer.querySelectorAll('select').forEach((select) => {
          if (select.closest('desktop-sticky-bar') || select.closest('[style*="display: none"]')) return;
          if (select.name && select.name.startsWith('options')) {
            selectedOptions.push(select.value);
          }
        });

        // Clear any previous size validation errors/messages when selection updates
        this.querySelectorAll('.size-error-message').forEach(el => el.remove());
        this.querySelectorAll('.size-validation-error').forEach(el => el.classList.remove('size-validation-error'));



        const script = this.querySelector('script[data-all-variants]');
        if (!script) return;
        let variants = [];
        try {
          variants = JSON.parse(script.textContent);
        } catch (e) {
          console.error(e);
          return;
        }

        // Check if size is required but not selected yet
        const sizeInputs = Array.from(activeContainer.querySelectorAll('input, select')).filter(el => {
          if (el.closest('desktop-sticky-bar') || el.closest('[style*="display: none"]')) return false;
          const opt = (el.getAttribute('data-option-name') || el.closest('fieldset')?.getAttribute('data-option-name') || '').toLowerCase();
          const nm  = (el.getAttribute('name') || '').toLowerCase();
          return opt.includes('size') || nm.includes('size');
        });
        const hasSizeField = sizeInputs.length > 0;
        const sizeSelected = !hasSizeField || sizeInputs.some(el =>
          el.tagName === 'SELECT' ? el.value !== '' : el.checked
        );

        let variant;
        if (sizeSelected && selectedOptions.length > 0) {
          variant = variants.find((v) => {
            return selectedOptions.every((val) =>
              v.options.some((opt) => String(opt).trim().toLowerCase() === String(val).trim().toLowerCase())
            );
          });
        }


        // 1. Update variant ID input in the cart form specifically
        const form = this.querySelector('form[action*="/cart/add"]');
        const variantInput = form ? form.querySelector('input[name="id"]') : this.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant ? variant.id : '';
          if (variant || !sizeSelected) {
            variantInput.removeAttribute('disabled');
          } else {
            variantInput.setAttribute('disabled', 'disabled');
          }
        }

        // Ensure price is visible inside the modal and updated dynamically with variant price
        const priceContainer = this.querySelector('[id^="price-"], .price');
        if (priceContainer) {
          priceContainer.classList.remove('hidden');
          if (variant && (variant.price !== undefined && variant.price !== null)) {
            const formatMoney = (amount) => {
              let val = typeof amount === 'number' ? amount : parseFloat(amount || 0);
              if (val > 1000) val = val / 100;
              const saleEl = priceContainer.querySelector('.price-item--sale') || priceContainer.querySelector('.price-item--regular');
              const text = saleEl ? saleEl.textContent.trim() : '';
              const match = text.match(/^([^\d\s,.]+)/) || text.match(/([^\d\s,.]+)$/);
              const symbol = match ? match[1] : '₹';
              const formatted = val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              return text.startsWith(symbol) ? `${symbol}${formatted}` : `${formatted} ${symbol}`;
            };

            const salePriceEl = priceContainer.querySelector('.price-item--sale') || priceContainer.querySelector('.price-item--last');
            const regularPriceEl = priceContainer.querySelector('.price-item--regular') || priceContainer.querySelector('s .price-item');

            if (salePriceEl) {
              salePriceEl.textContent = formatMoney(variant.price);
            }
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              priceContainer.classList.add('price--on-sale');
              if (regularPriceEl) regularPriceEl.textContent = formatMoney(variant.compare_at_price);
            } else {
              priceContainer.classList.remove('price--on-sale');
              if (regularPriceEl && regularPriceEl !== salePriceEl) regularPriceEl.textContent = formatMoney(variant.price);
            }
          }
        }

        // 2. Update Submit (Add to Cart) button (both disabled & aria-disabled)
        const submitButtons = this.querySelectorAll('.product-form__submit, [type="submit"][name="add"]');
        submitButtons.forEach((submitButton) => {
          if (!sizeSelected) {
            if (this.dataset.productAvailable === 'false') {
              submitButton.setAttribute('disabled', 'disabled');
              submitButton.setAttribute('aria-disabled', 'true');
              const spanText = submitButton.querySelector('span');
              if (spanText) spanText.textContent = window.variantStrings?.soldOut || 'Sold out';
            } else {
              // No size selected yet: Keep button enabled so clicking it triggers validation
              submitButton.removeAttribute('disabled');
              submitButton.removeAttribute('aria-disabled');
              const spanText = submitButton.querySelector('span');
              if (spanText) spanText.textContent = window.variantStrings?.addToCart || 'Add to Cart';
            }
          } else if (!variant) {
            // Size selected, but variant does not exist (Unavailable)
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.setAttribute('aria-disabled', 'true');
            const spanText = submitButton.querySelector('span');
            if (spanText) spanText.textContent = window.variantStrings?.unavailable || 'Unavailable';
          } else if (variant.available) {
            // Variant exists and is in stock
            submitButton.removeAttribute('disabled');
            submitButton.removeAttribute('aria-disabled');
            const spanText = submitButton.querySelector('span');
            if (spanText) spanText.textContent = window.variantStrings?.addToCart || 'Add to Cart';
          } else {
            // Variant exists but is out of stock (Sold out)
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.setAttribute('aria-disabled', 'true');
            const spanText = submitButton.querySelector('span');
            if (spanText) {
              spanText.textContent = window.variantStrings?.soldOut || 'Sold out';
            }
          }
        });



        // 3. Publish variant change event
        if (variant) {
          publish(PUB_SUB_EVENTS.variantChange, {
            detail: {
              sectionId: this.sectionId,
              html: null,
              variant,
            },
            data: {
              sectionId: this.sectionId,
              html: null,
              variant,
            },
          });
        }
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            if (!window._stickyBarChanging) {
              document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        const root = productInfoNode || this;
        const activeContainer = this.getActiveVariantSelects(root);
        const variant = this.parseJsonScript(activeContainer.tagName === 'VARIANT-SELECTS' ? activeContainer : this.getVariantSelects(root), '[data-selected-variant]');
        if (variant) return variant;

        // Fallback: If data-selected-variant is null, resolve variant from selected options & data-all-variants
        const allVariantsScript = root.querySelector('script[data-all-variants]') || this.querySelector('script[data-all-variants]');
        if (!allVariantsScript) return null;
        try {
          const variants = JSON.parse(allVariantsScript.textContent);
          const selectedOptions = [];
          activeContainer.querySelectorAll('fieldset.product-form__input, fieldset[data-option-name], fieldset').forEach((group) => {
            if (group.closest('desktop-sticky-bar') || group.closest('[style*="display: none"]')) return;
            const checked = group.querySelector('input:checked');
            if (checked) selectedOptions.push(checked.value);
          });
          activeContainer.querySelectorAll('select').forEach((select) => {
            if (select.closest('desktop-sticky-bar') || select.closest('[style*="display: none"]')) return;
            if (select.name && select.name.startsWith('options')) {
              if (select.value) selectedOptions.push(select.value);
            }
          });
          if (selectedOptions.length > 0) {
            return variants.find((v) =>
              selectedOptions.every((val) =>
                v.options.some((opt) => String(opt).trim().toLowerCase() === String(val).trim().toLowerCase())
              )
            );
          }
        } catch (e) {
          console.error(e);
        }
        return null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        const currentVariant = this.getSelectedVariant(this);
        if (currentVariant && currentVariant.id) {
          params.push(`variant=${currentVariant.id}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html) || this.getSelectedVariant(this);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          // Update Buy Now button wrapper visibility inside quick-add-modal dynamically on variant change
          const buyNowWrapper = this.querySelector('.product-form__buy-now-wrapper');

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          // Run instant local update to keep submit button state locked to selected variant options
          this.handleLocalVariantUpdate();

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          publish(PUB_SUB_EVENTS.variantChange, {
            detail: {
              sectionId: this.sectionId,
              html,
              variant,
            },
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            if (variantId) {
              input.removeAttribute('disabled');
            } else {
              input.setAttribute('disabled', 'disabled');
            }
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal && (!quickAddModal.hasAttribute('open') || quickAddModal.classList.contains('closing'))) {
          return;
        }

        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }


    }
  );
}
